import { ApiClient, AuthErrorCode, SESSION_ERROR_CODES } from "@netmountains/shared/browser";

import { API_BASE_URL } from "../config/constants";
import { isAuthorizationError, notifyAuthorizationError } from "../utils/apiErrors";

/**
 * Creates a configured API client for the frontend
 */
const createApiClient = () => {
  // Track refresh token promise to prevent multiple simultaneous refresh requests
  let isRefreshing = false;
  let refreshPromise = null;
  const failedQueue = [];

  // Process queued requests after token refresh
  const processQueue = error => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });

    failedQueue.splice(0, failedQueue.length);
  };

  // Helper to get CSRF token from cookies
  const getCsrfToken = () => {
    const cookies = document.cookie.split(";");

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "scopex_csrf_token") {
        return value;
      }
    }
    return null;
  };

  // Create API client with cookie-based configuration
  const apiClient = new ApiClient({
    baseURL: API_BASE_URL,
    timeout: 60000,
    withCredentials: true, // Essential for cookie-based auth
  });

  // Add interceptor to handle FormData and CSRF tokens correctly
  apiClient.axiosInstance &&
    apiClient.axiosInstance.interceptors.request.use(config => {
      console.log("[Frontend ApiClient] Request:", config.method, config.url);

      config.headers = config.headers ?? {};

      if (config.data instanceof FormData) {
        console.log("[Frontend ApiClient] Request contains FormData, removing Content-Type header");
        delete config.headers["Content-Type"];
      }

      if (isStateChanging(config.method)) {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          console.log("[Frontend ApiClient] Added CSRF token to headers:", csrfToken);
          config.headers["X-CSRF-Token"] = csrfToken;
        } else {
          console.warn("[Frontend ApiClient] ⚠️ No CSRF token available!");
        }
      }

      return config;
    });

  // Add getCsrfToken method for components that need it
  apiClient.getCsrfToken = getCsrfToken;

  // Simple refresh token method
  apiClient.refreshToken = async () => {
    console.log("[Frontend ApiClient] 🔄 Refreshing token");
    try {
      if (isRefreshing) {
        console.log(
          "[Frontend ApiClient] Token refresh already in progress, returning existing promise",
        );
        return refreshPromise;
      }

      isRefreshing = true;
      console.log("[Frontend ApiClient] Starting token refresh request");

      refreshPromise = apiClient.post("/auth/refresh", {});

      const response = await refreshPromise;
      console.log("[Frontend ApiClient] ✅ Token refresh successful");

      isRefreshing = false;
      processQueue(null);

      // ✅ Wait a tiny bit for cookies to be set by the browser
      await new Promise(resolve => setTimeout(resolve, 50));

      console.log("[Frontend ApiClient] 🍪 New CSRF token after refresh:", getCsrfToken());

      // Return the token data for immediate use
      return response.data;
    } catch (error) {
      console.error("[Frontend ApiClient] ❌ Token refresh failed:", error.message);

      isRefreshing = false;
      processQueue(error);

      // ✅ Check for specific error codes
      const errorCode = error.response?.data?.code;
      const status = error.response?.status;

      if (SESSION_ERROR_CODES.includes(errorCode) || status === 401 || status === 403) {
        console.error("[Frontend ApiClient] 🚪 Session invalid, redirecting to login");

        // ✅ Clear local storage
        localStorage.clear();

        // ✅ Redirect to login (only if not already there)
        if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
          console.log("[Frontend ApiClient] Redirecting from:", window.location.pathname);
          window.location.href = "/login";
        }
      }

      throw error;
    }
  };

  // Add response interceptor
  apiClient.axiosInstance &&
    apiClient.axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        if (!error.response) {
          console.error(
            "[Frontend ApiClient] 🚨 Network error",
            JSON.stringify({
              message: error.message,
              code: error.code,
              url: error.config?.url,
              method: error.config?.method,
              data: serializePayload(error.config?.data),
            }),
          );
          return Promise.reject(error);
        }

        const originalRequest = error.config;

        // ✅ Skip refresh for login/signup endpoints
        if (
          originalRequest.url?.includes("/auth/login") ||
          originalRequest.url?.includes("/auth/signup") ||
          originalRequest.url?.includes("/auth/refresh")
        ) {
          console.log(
            "[Frontend ApiClient] Skipping refresh for auth endpoint:",
            originalRequest.url,
          );
          return Promise.reject(error);
        }

        // Handle 401 (Unauthorized - token expired)
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            if (isRefreshing) {
              console.log("[Frontend ApiClient] ⏳ Waiting for token refresh to complete");
              return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
              })
                .then(() => {
                  console.log("[Frontend ApiClient] ✅ Token refresh completed, retrying request");

                  const retryConfig = {
                    ...originalRequest,
                    headers: { ...(originalRequest.headers ?? {}) },
                  };

                  if (isStateChanging(retryConfig.method)) {
                    const csrfToken = getCsrfToken();
                    console.log("[Frontend ApiClient] 🔑 Using refreshed CSRF token:", csrfToken);
                    if (csrfToken) {
                      retryConfig.headers["X-CSRF-Token"] = csrfToken;
                    }
                  }

                  return apiClient.axiosInstance.request(retryConfig);
                })
                .catch(err => {
                  // ✅ If queued request fails, also redirect
                  console.error("[Frontend ApiClient] Queued request failed:", err.message);
                  if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
                    window.location.href = "/login";
                  }
                  return Promise.reject(err);
                });
            }

            console.log("[Frontend ApiClient] 🔄 Starting token refresh");

            // Get new tokens from refresh
            await apiClient.refreshToken(); // ✅ This will throw and redirect if token not found

            console.log(
              "[Frontend ApiClient] ✅ Token refresh successful, retrying original request",
            );

            const retryConfig = {
              ...originalRequest,
              headers: { ...(originalRequest.headers ?? {}) },
            };

            if (isStateChanging(retryConfig.method)) {
              const csrfToken = getCsrfToken();
              console.log("[Frontend ApiClient] 🔑 Using new CSRF token:", csrfToken);
              if (csrfToken) {
                retryConfig.headers["X-CSRF-Token"] = csrfToken;
              } else {
                console.error("[Frontend ApiClient] ⚠️ No CSRF token found after refresh!");
              }
            }

            return apiClient.axiosInstance.request(retryConfig);
          } catch (refreshError) {
            console.error("[Frontend ApiClient] ❌ Token refresh failed, redirecting to login");

            // ✅ Clear all auth data
            localStorage.clear();

            // ✅ Redirect to login
            if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
              window.location.href = "/login";
            }

            // ✅ Return a rejected promise instead of throwing
            // This prevents "Unhandled Promise Rejection"
            return Promise.reject(refreshError);
          }
        }

        // Handle 403 — either a broken session (token reuse / CSRF) or a role rejection.
        if (error.response?.status === 403) {
          const errorCode = error.response?.data?.code;

          // ✅ Check if it's a token reuse error
          if (errorCode === AuthErrorCode.TOKEN_REUSE_DETECTED) {
            console.error("[Frontend ApiClient] 🚨 Token reuse detected!");
            localStorage.clear();
            window.location.href = "/login";
            return Promise.reject(error);
          }

          // Matched on `code` — this used to test `message.includes("CSRF")`, but the API sends
          // "Cross-site request validation failed", so the branch never ran and a CSRF mismatch
          // just failed silently.
          if (errorCode === AuthErrorCode.CSRF_INVALID) {
            console.error("[Frontend ApiClient] ❌ CSRF validation failed!");

            // ✅ Try to get fresh CSRF token and retry ONCE
            if (!originalRequest._csrfRetry) {
              originalRequest._csrfRetry = true;

              console.log("[Frontend ApiClient] 🔄 Retrying with fresh CSRF token");

              // Wait a bit for cookies to settle
              await new Promise(resolve => setTimeout(resolve, 100));

              const freshCsrfToken = getCsrfToken();
              if (freshCsrfToken) {
                const retryConfig = {
                  ...originalRequest,
                  headers: { ...(originalRequest.headers ?? {}) },
                };
                retryConfig.headers["X-CSRF-Token"] = freshCsrfToken;
                return apiClient.axiosInstance.request(retryConfig);
              }
            }

            // Still failing after a retry: the session's CSRF cookie is unusable. Send the user to
            // login rather than reloading — a reload would just fail the same way, in a loop.
            console.error("[Frontend ApiClient] ⚠️ CSRF token still invalid, session unusable");
            localStorage.clear();
            if (window.location.pathname !== "/login" && window.location.pathname !== "/") {
              window.location.href = "/login";
            }
            return Promise.reject(error);
          }

          // Every other 403 is the `authorize` role gate on the backend. Nobody used to handle it,
          // so the page rendered without its data and crashed. Tag the error and broadcast it, so
          // the app-wide notice explains it even when the caller ignores the rejection.
          if (isAuthorizationError(error)) {
            console.warn("[Frontend ApiClient] ⛔ Not authorized for", originalRequest.url);
            error.isAuthorizationError = true;
            notifyAuthorizationError(error);
          }
        }

        return Promise.reject(error);
      },
    );

  return apiClient;
};

// Create singleton instance
const apiClient = createApiClient();

export default apiClient;

const isStateChanging = method =>
  ["post", "put", "delete", "patch"].includes(method?.toLowerCase());

const serializePayload = payload => {
  if (!payload) return undefined;
  if (payload instanceof FormData) return "[FormData]";
  if (typeof payload === "string")
    return payload.length > 1000 ? `${payload.slice(0, 1000)}…` : payload;
  try {
    const json = JSON.stringify(payload);
    return json.length > 1000 ? `${json.slice(0, 1000)}…` : json;
  } catch {
    return "[Unserializable payload]";
  }
};
